import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Badge
} from '@mui/material';
import {
  CloudDownload as DownloadIcon,
  AutoMode as AutoIcon,
  Edit as ManualIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  AccountBalance as BankIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { RemittanceAdvice, ClaimPayment, LinePayment, AdjustmentReason } from '../../types/Billing';

const RemittancePosting: React.FC = () => {
  const [remittances, setRemittances] = useState<RemittanceAdvice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [payerFilter, setPayerFilter] = useState<string>('all');
  const [autoPostingEnabled, setAutoPostingEnabled] = useState(true);
  const [openRemittanceDialog, setOpenRemittanceDialog] = useState(false);
  const [selectedRemittance, setSelectedRemittance] = useState<RemittanceAdvice | null>(null);
  const [processingRemittance, setProcessingRemittance] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Mock data - in real app, this would be an API call
      const mockRemittances: RemittanceAdvice[] = [
        {
          id: 'era-001',
          payerId: 'PAYER-001',
          checkNumber: 'CHK-2024-001',
          checkDate: '2024-01-20T00:00:00Z',
          checkAmount: 1250.50,
          paymentMethod: 'eft',
          era835Id: 'ERA-001-2024',
          processingDate: '2024-01-20T09:00:00Z',
          status: 'posted',
          autoPosted: true,
          reviewRequired: false,
          claimPayments: [
            {
              id: 'cp-001',
              claimId: 'claim-001',
              claimNumber: 'CLM-2024-001',
              patientId: 'PAT-001',
              serviceDate: '2024-01-15T08:00:00Z',
              totalCharges: 110.00,
              totalPayments: 63.06,
              totalAdjustments: 46.94,
              patientResponsibility: 31.18,
              paymentStatus: 'paid',
              linePayments: [
                {
                  id: 'lp-001',
                  claimLineId: 'line-001',
                  cptCode: '85025',
                  modifiers: [],
                  billedAmount: 45.00,
                  allowedAmount: 32.19,
                  paidAmount: 25.75,
                  adjustmentAmount: 12.81,
                  deductibleAmount: 0,
                  coinsuranceAmount: 6.44,
                  copayAmount: 0,
                  adjustmentReasons: [
                    {
                      code: 'CO-42',
                      description: 'Charges exceed your contracted/legislated fee arrangement.',
                      amount: 12.81,
                      type: 'contractual'
                    }
                  ],
                  remarkCodes: []
                },
                {
                  id: 'lp-002',
                  claimLineId: 'line-002',
                  cptCode: '80053',
                  modifiers: [],
                  billedAmount: 65.00,
                  allowedAmount: 46.63,
                  paidAmount: 37.31,
                  adjustmentAmount: 18.37,
                  deductibleAmount: 0,
                  coinsuranceAmount: 9.32,
                  copayAmount: 0,
                  adjustmentReasons: [
                    {
                      code: 'CO-42',
                      description: 'Charges exceed your contracted/legislated fee arrangement.',
                      amount: 18.37,
                      type: 'contractual'
                    }
                  ],
                  remarkCodes: []
                }
              ]
            }
          ],
          createdAt: '2024-01-20T09:00:00Z',
          updatedAt: '2024-01-20T09:30:00Z'
        },
        {
          id: 'era-002',
          payerId: 'PAYER-002',
          checkNumber: 'CHK-2024-002',
          checkDate: '2024-01-21T00:00:00Z',
          checkAmount: 890.75,
          paymentMethod: 'check',
          processingDate: '2024-01-21T14:00:00Z',
          status: 'received',
          autoPosted: false,
          reviewRequired: true,
          claimPayments: [
            {
              id: 'cp-002',
              claimId: 'claim-002',
              claimNumber: 'CLM-2024-002',
              patientId: 'PAT-002',
              serviceDate: '2024-01-16T09:00:00Z',
              totalCharges: 125.00,
              totalPayments: 0,
              totalAdjustments: 125.00,
              patientResponsibility: 0,
              paymentStatus: 'denied',
              linePayments: [
                {
                  id: 'lp-003',
                  claimLineId: 'line-003',
                  cptCode: '85025',
                  modifiers: [],
                  billedAmount: 45.00,
                  allowedAmount: 0,
                  paidAmount: 0,
                  adjustmentAmount: 45.00,
                  deductibleAmount: 0,
                  coinsuranceAmount: 0,
                  copayAmount: 0,
                  adjustmentReasons: [
                    {
                      code: 'CO-16',
                      description: 'Claim/service lacks information or has submission/billing error(s).',
                      amount: 45.00,
                      type: 'denial'
                    }
                  ],
                  remarkCodes: ['N382']
                }
              ]
            }
          ],
          createdAt: '2024-01-21T14:00:00Z',
          updatedAt: '2024-01-21T14:00:00Z'
        }
      ];

      setRemittances(mockRemittances);
    } catch (error) {
      console.error('Error loading remittance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'received':
        return 'warning';
      case 'posted':
        return 'success';
      case 'reviewed':
        return 'info';
      case 'exception':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'denied':
        return 'error';
      case 'partial':
        return 'warning';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'eft':
        return <BankIcon color="primary" />;
      case 'check':
        return <AssignmentIcon color="secondary" />;
      case 'card':
        return <PaymentIcon color="success" />;
      default:
        return <PaymentIcon />;
    }
  };

  const filteredRemittances = remittances.filter(remittance => {
    const matchesSearch = remittance.checkNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         remittance.payerId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || remittance.status === statusFilter;
    const matchesPayer = payerFilter === 'all' || remittance.payerId === payerFilter;
    return matchesSearch && matchesStatus && matchesPayer;
  });

  const handleViewRemittance = (remittance: RemittanceAdvice) => {
    setSelectedRemittance(remittance);
    setOpenRemittanceDialog(true);
  };

  const handleAutoPost = async (remittanceId: string) => {
    setProcessingRemittance(true);
    setProcessingProgress(0);

    // Simulate auto-posting process
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      setProcessingProgress(i);
    }

    setProcessingRemittance(false);
    setProcessingProgress(0);
    
    // Update remittance status
    setRemittances(prev => prev.map(r => 
      r.id === remittanceId 
        ? { ...r, status: 'posted', autoPosted: true }
        : r
    ));
  };

  const getReceivedCount = () => {
    return remittances.filter(r => r.status === 'received').length;
  };

  const getExceptionCount = () => {
    return remittances.filter(r => r.status === 'exception' || r.reviewRequired).length;
  };

  const getTotalAmount = () => {
    return remittances.reduce((sum, r) => sum + r.checkAmount, 0);
  };

  const getAutoPostedCount = () => {
    return remittances.filter(r => r.autoPosted).length;
  };

  const uniquePayers = Array.from(new Set(remittances.map(r => r.payerId)));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Remittance & ERA Posting
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            Download ERA Files
          </Button>
          <Button
            variant="contained"
            startIcon={autoPostingEnabled ? <AutoIcon /> : <ManualIcon />}
            color={autoPostingEnabled ? 'success' : 'warning'}
            onClick={() => setAutoPostingEnabled(!autoPostingEnabled)}
          >
            {autoPostingEnabled ? 'Auto-Posting ON' : 'Auto-Posting OFF'}
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PaymentIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Total Remittances
                </Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {remittances.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(getTotalAmount())} total amount
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Pending Review
                </Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {getReceivedCount()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Awaiting processing
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AutoIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Auto-Posted
                </Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {getAutoPostedCount()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Automatically processed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Exceptions
                </Typography>
              </Box>
              <Typography variant="h4" color="error.main">
                {getExceptionCount()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Require manual review
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Processing Progress */}
      {processingRemittance && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Auto-Posting in Progress
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={processingProgress} 
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {processingProgress}% complete
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Remittances Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              label="Search remittances"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
              sx={{ minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="received">Received</MenuItem>
                <MenuItem value="posted">Posted</MenuItem>
                <MenuItem value="reviewed">Reviewed</MenuItem>
                <MenuItem value="exception">Exception</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Payer</InputLabel>
              <Select
                value={payerFilter}
                label="Payer"
                onChange={(e) => setPayerFilter(e.target.value)}
              >
                <MenuItem value="all">All Payers</MenuItem>
                {uniquePayers.map((payer) => (
                  <MenuItem key={payer} value={payer}>
                    {payer}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Check Number</TableCell>
                  <TableCell>Payer</TableCell>
                  <TableCell>Check Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Auto-Posted</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRemittances.map((remittance) => (
                  <TableRow key={remittance.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {remittance.checkNumber}
                      </Typography>
                      {remittance.era835Id && (
                        <Typography variant="caption" color="text.secondary">
                          ERA: {remittance.era835Id}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{remittance.payerId}</TableCell>
                    <TableCell>{formatDate(remittance.checkDate)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(remittance.checkAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getPaymentMethodIcon(remittance.paymentMethod)}
                        <Typography variant="body2">
                          {remittance.paymentMethod.toUpperCase()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={remittance.status.charAt(0).toUpperCase() + remittance.status.slice(1)}
                          color={getStatusColor(remittance.status) as any}
                          size="small"
                        />
                        {remittance.reviewRequired && (
                          <Badge color="error" variant="dot">
                            <WarningIcon fontSize="small" color="warning" />
                          </Badge>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={remittance.autoPosted ? 'Yes' : 'No'}
                        color={remittance.autoPosted ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewRemittance(remittance)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {remittance.status === 'received' && (
                          <Tooltip title="Auto-Post">
                            <IconButton 
                              size="small" 
                              onClick={() => handleAutoPost(remittance.id)}
                              disabled={processingRemittance}
                            >
                              <AutoIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Remittance Details Dialog */}
      <Dialog 
        open={openRemittanceDialog} 
        onClose={() => setOpenRemittanceDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Remittance Details: {selectedRemittance?.checkNumber}
        </DialogTitle>
        <DialogContent>
          {selectedRemittance && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Payment Information
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Payer ID" 
                        secondary={selectedRemittance.payerId} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Check Date" 
                        secondary={formatDate(selectedRemittance.checkDate)} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Check Amount" 
                        secondary={formatCurrency(selectedRemittance.checkAmount)} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Payment Method" 
                        secondary={selectedRemittance.paymentMethod.toUpperCase()} 
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Processing Status
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Status" 
                        secondary={
                          <Chip 
                            label={selectedRemittance.status}
                            color={getStatusColor(selectedRemittance.status) as any}
                            size="small"
                          />
                        } 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Auto-Posted" 
                        secondary={selectedRemittance.autoPosted ? 'Yes' : 'No'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Review Required" 
                        secondary={selectedRemittance.reviewRequired ? 'Yes' : 'No'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Processing Date" 
                        secondary={formatDateTime(selectedRemittance.processingDate)} 
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Claim Payments
              </Typography>
              {selectedRemittance.claimPayments.map((claimPayment, index) => (
                <Accordion key={claimPayment.id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                      <Typography variant="body1" fontWeight="medium">
                        Claim: {claimPayment.claimNumber}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Chip 
                          label={claimPayment.paymentStatus}
                          color={getPaymentStatusColor(claimPayment.paymentStatus) as any}
                          size="small"
                        />
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(claimPayment.totalPayments)}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Patient ID</Typography>
                        <Typography variant="body2">{claimPayment.patientId}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Service Date</Typography>
                        <Typography variant="body2">{formatDate(claimPayment.serviceDate)}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" color="text.secondary">Total Charges</Typography>
                        <Typography variant="body2">{formatCurrency(claimPayment.totalCharges)}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" color="text.secondary">Total Payments</Typography>
                        <Typography variant="body2">{formatCurrency(claimPayment.totalPayments)}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" color="text.secondary">Total Adjustments</Typography>
                        <Typography variant="body2">{formatCurrency(claimPayment.totalAdjustments)}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" color="text.secondary">Patient Responsibility</Typography>
                        <Typography variant="body2">{formatCurrency(claimPayment.patientResponsibility)}</Typography>
                      </Grid>
                    </Grid>

                    <Typography variant="subtitle2" gutterBottom>
                      Line Item Details
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>CPT Code</TableCell>
                            <TableCell align="right">Billed</TableCell>
                            <TableCell align="right">Allowed</TableCell>
                            <TableCell align="right">Paid</TableCell>
                            <TableCell align="right">Adjustment</TableCell>
                            <TableCell>Adjustment Reasons</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {claimPayment.linePayments.map((linePayment) => (
                            <TableRow key={linePayment.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {linePayment.cptCode}
                                </Typography>
                                {linePayment.modifiers.length > 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    Mods: {linePayment.modifiers.join(', ')}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(linePayment.billedAmount)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(linePayment.allowedAmount)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(linePayment.paidAmount)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(linePayment.adjustmentAmount)}
                              </TableCell>
                              <TableCell>
                                {linePayment.adjustmentReasons.map((reason, idx) => (
                                  <Tooltip key={idx} title={reason.description}>
                                    <Chip 
                                      label={reason.code}
                                      size="small"
                                      variant="outlined"
                                      sx={{ mr: 0.5, mb: 0.5 }}
                                    />
                                  </Tooltip>
                                ))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRemittanceDialog(false)}>Close</Button>
          {selectedRemittance?.status === 'received' && (
            <Button 
              variant="contained" 
              startIcon={<AutoIcon />}
              onClick={() => selectedRemittance && handleAutoPost(selectedRemittance.id)}
            >
              Auto-Post
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RemittancePosting;